/**
 * Gets a nested value from an object using dot notation
 * @param {Object} obj - The object to search
 * @param {string} path - The dot-separated path (e.g., 'checkout.orderSummary.title')
 * @param {*} defaultValue - Default value if path not found
 * @returns {*} The value at the path or defaultValue
 */
export const getNestedValue = (obj, path, defaultValue = null) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
};

/**
 * Interpolates variables in a translation string
 * @param {string} template - The template string with {{variable}} placeholders
 * @param {Object} variables - Object containing variable values
 * @returns {string} The interpolated string
 */
export const interpolateString = (template, variables = {}) => {
  if (typeof template !== 'string') return template;
  
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
};

/**
 * Formats currency based on language/locale
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (BRL, USD, etc.)
 * @param {string} language - Language code
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency, language) => {
  const localeMap = {
    en: 'en-US',
    pt_br: 'pt-BR'
  };
  
  try {
    return new Intl.NumberFormat(localeMap[language] || 'en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  } catch (error) {
    // Fallback for unsupported currencies
    const symbol = currency === 'BRL' ? 'R$' : '$';
    return `${symbol} ${amount.toFixed(2)}`;
  }
};

/**
 * Converts minor units to major units based on currency fraction digits.
 * Example: USD -> 1234 => 12.34, KRW -> 1234 => 1234.
 *
 * @param {number} amountMinor - Amount in minor units
 * @param {string} currency - ISO currency code
 * @returns {number} Amount in major units
 */
export const minorToMajor = (amountMinor, currency) => {
  const normalizedAmount = Number(amountMinor || 0);
  const normalizedCurrency = (currency || "USD").toUpperCase();

  try {
    const { maximumFractionDigits } = new Intl.NumberFormat("en", {
      style: "currency",
      currency: normalizedCurrency,
    }).resolvedOptions();

    const divisor = 10 ** maximumFractionDigits;
    return normalizedAmount / divisor;
  } catch (error) {
    // Fallback to 2-decimal behavior for unsupported currencies
    return normalizedAmount / 100;
  }
};

/**
 * Formats date based on language/locale
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code
 * @returns {string} Formatted date string
 */
export const formatDate = (date, language) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const localeMap = {
    en: 'en-US',
    pt_br: 'pt-BR'
  };
  
  try {
    return new Intl.DateTimeFormat(localeMap[language] || 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(dateObj);
  } catch (error) {
    return dateObj.toDateString();
  }
};

/**
 * Gets the appropriate plural form for a count (basic implementation)
 * @param {number} count - The count
 * @param {Object} pluralForms - Object with singular/plural forms
 * @param {string} language - Language code
 * @returns {string} The appropriate plural form
 */
export const getPluralForm = (count, pluralForms, language) => {
  if (typeof pluralForms === 'string') return pluralForms;
  
  const { singular, plural } = pluralForms;
  
  // Basic pluralization rules - can be extended for more complex languages
  if (language === 'pt_br') {
    return count === 1 ? singular : plural;
  }
  
  // Default English pluralization
  return count === 1 ? singular : plural;
}; 